import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { X } from "lucide-react";
import { forwardRef } from "react";
import { cn } from "@/lib/utils";

export interface DrawerProps {
	open: boolean;
	onClose?: () => void;
	title?: React.ReactNode;
	placement?: "left" | "right" | "top" | "bottom";
	width?: number | string;
	height?: number | string;
	closable?: boolean;
	maskClosable?: boolean;
	destroyOnClose?: boolean;
	footer?: React.ReactNode;
	extra?: React.ReactNode;
	children?: React.ReactNode;
	className?: string;
	styles?: {
		body?: React.CSSProperties;
		header?: React.CSSProperties;
		footer?: React.CSSProperties;
		mask?: React.CSSProperties;
		wrapper?: React.CSSProperties;
	};
	zIndex?: number;
	push?: boolean | { distance: number };
}

const Drawer = forwardRef<HTMLDivElement, DrawerProps>(
	(
		{
			open,
			onClose,
			title,
			placement = "right",
			width = 378,
			height = 378,
			closable = true,
			maskClosable = true,
			destroyOnClose = false,
			footer,
			extra,
			children,
			className,
			styles,
			zIndex = 1000,
			push: _push,
		},
		ref,
	) => {
		const handleClose = () => {
			if (onClose) {
				onClose();
			}
		};

		const isHorizontal = placement === "left" || placement === "right";
		const size = isHorizontal ? width : height;
		const sizeValue = typeof size === "number" ? `${size}px` : size;

		const placementClasses = {
			left: "left-0 top-0 bottom-0 data-[closed]:-translate-x-full",
			right: "right-0 top-0 bottom-0 data-[closed]:translate-x-full",
			top: "top-0 left-0 right-0 data-[closed]:-translate-y-full",
			bottom: "bottom-0 left-0 right-0 data-[closed]:translate-y-full",
		};

		const placementStyles = {
			left: { width: sizeValue },
			right: { width: sizeValue },
			top: { height: sizeValue },
			bottom: { height: sizeValue },
		};

		if (destroyOnClose && !open) {
			return null;
		}

		return (
			<Dialog
				open={open}
				onClose={maskClosable ? handleClose : () => {}}
				className="relative"
				style={{ zIndex }}
			>
				{/* Backdrop */}
				<DialogBackdrop
					transition
					className={cn(
						"fixed inset-0 bg-black/45",
						"transition duration-300 ease-out",
						"data-[closed]:opacity-0",
					)}
					style={styles?.mask}
				/>

				{/* Panel */}
				<div className="fixed inset-0 overflow-hidden">
					<div className="absolute inset-0 overflow-hidden">
						<DialogPanel
							ref={ref}
							transition
							className={cn(
								"fixed bg-white dark:bg-zinc-800 shadow-xl flex flex-col",
								"transition duration-300 ease-out",
								"data-[closed]:transform",
								placementClasses[placement],
								className,
							)}
							style={{
								...placementStyles[placement],
								...styles?.wrapper,
							}}
						>
							{/* Header */}
							{(title || closable || extra) && (
								<div
									className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-700 flex-shrink-0"
									style={styles?.header}
								>
									<DialogTitle className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
										{title}
									</DialogTitle>
									<div className="flex items-center gap-2">
										{extra}
										{closable && (
											<button
												type="button"
												onClick={handleClose}
												className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
											>
												<X className="h-5 w-5" />
											</button>
										)}
									</div>
								</div>
							)}

							{/* Body */}
							<div className="flex-1 px-6 py-4 overflow-auto" style={styles?.body}>
								{children}
							</div>

							{/* Footer */}
							{footer && (
								<div
									className="px-6 py-4 border-t border-zinc-200 dark:border-zinc-700 flex-shrink-0"
									style={styles?.footer}
								>
									{footer}
								</div>
							)}
						</DialogPanel>
					</div>
				</div>
			</Dialog>
		);
	},
);

Drawer.displayName = "Drawer";

export { Drawer };
